/** GLP-1 Led Longevity Program — page copy, signals and section data. */
import type { SignalMarqueeItem } from '../DiagnosticsSignalMarquee';
import {
  GLP_GUIDED_IMAGE_SRC,
  GLP_HERO_BACKGROUND,
  GLP_JOURNEY_CHAT_BG_SRC,
  GLP_LONGEVITY_IMAGE_SRC,
  GLP_SCIENCE_IMAGE_SRC,
  GLP_SCIENCE_IMAGE_SRC_MOBILE,
} from './glpPageAssets';

export { GLP_HERO_BACKGROUND };

export const GLP_MARQUEE_PREFIX = 'glp.marquee';

/** Bottom hero marquee — what the program protects. */
export const GLP_MARQUEE_SIGNALS: ReadonlyArray<SignalMarqueeItem> = [
  { key: 'fatLoss', default: 'fat loss' },
  { key: 'musclePreservation', default: 'MUSCLE PRESERVATION' },
  { key: 'heartHealth', default: 'hEART HEALTH' },
  { key: 'metabolicHealth', default: 'METABOLIC HEALTH' },
  { key: 'brainHealth', default: 'brain health' },
  { key: 'strengthRetention', default: 'strength RETENTION' },
  { key: 'longevity', default: 'LONGEVITY' },
];

export type GlpStackCard = {
  id: string;
  labelKey: string;
  label: string;
  titleKey: string;
  title: string;
  bodyKey: string;
  body: string;
  imageLabel: string;
  imageSrc: string;
};

/** Longevity Protocol — scroll-stacking cards. */
export const GLP_LONGEVITY_CARDS: ReadonlyArray<GlpStackCard> = [
  {
    id: 'measurement',
    labelKey: 'glp.longevity.cards.measurement.label',
    label: 'measurement',
    titleKey: 'glp.longevity.cards.measurement.title',
    title: 'Track what you actually lost',
    bodyKey: 'glp.longevity.cards.measurement.body',
    body: 'The scale tells you that you lost weight. It cannot tell you what you lost. So we track every system the drug acts on – body composition, bone density, blood, vision, heart, grip, blood pressure, glucose, VO₂ max and cognition. The full picture, from first dose to last.',
    imageLabel: 'Performance score',
    imageSrc: GLP_LONGEVITY_IMAGE_SRC.measurement,
  },
  {
    id: 'guidance',
    labelKey: 'glp.longevity.cards.guidance.label',
    label: 'guidance',
    titleKey: 'glp.longevity.cards.guidance.title',
    title: 'Guided by a clinician',
    bodyKey: 'glp.longevity.cards.guidance.body',
    body: 'A physician and a coach own the therapy, not a chatbot and a default dose. They read your results, titrate only when your data says to, and change course as your body responds. Longevity Doctor consultations, 1-1 nutrition plans, and performance coaching at every step.',
    imageLabel: 'Clinician consult',
    imageSrc: GLP_LONGEVITY_IMAGE_SRC.guidance,
  },
  {
    id: 'interventions',
    labelKey: 'glp.longevity.cards.interventions.label',
    label: 'interventions',
    titleKey: 'glp.longevity.cards.interventions.title',
    title: 'Keep the loss coming from fat',
    bodyKey: 'glp.longevity.cards.interventions.body',
    body: 'The loss should come from fat. These are the inputs that help keep muscle on while it does: a resistance training program built to help preserve muscle, high-protein personalized nutrition to give lean mass its best shot, and a muscle preservation protocol that catches loss early, before it becomes muscle you have to win back.',
    imageLabel: 'Weekly review',
    imageSrc: GLP_LONGEVITY_IMAGE_SRC.interventions,
  },
  {
    id: 'response',
    labelKey: 'glp.longevity.cards.response.label',
    label: 'RESPONSE',
    titleKey: 'glp.longevity.cards.response.title',
    title: 'Continuous Glucose Monitoring',
    bodyKey: 'glp.longevity.cards.response.body',
    body: "Glucose is one of the systems the medication moves most, and a single fasting reading isn't enough. The Ultrahuman M1 Live CGM tracks it continuously as part of the program. Get guided feedback of your metabolic health by a support team consisting of a nutritionist, a performance coach and a longevity physician.\n\n*CGM inclusions vary by plan.",
    imageLabel: 'Continuous glucose monitoring',
    imageSrc: GLP_LONGEVITY_IMAGE_SRC.response,
  },
];

export type GlpCarouselCard = {
  id: string;
  labelKey: string;
  label: string;
  titleKey: string;
  title: string;
  bodyKey: string;
  body: string;
  imageLabel: string;
  imageSrc: string;
};

/** Data-Driven Guided Protocol — embla carousel cards. */
export const GLP_GUIDED_CARDS: ReadonlyArray<GlpCarouselCard> = [
  {
    id: 'doctor-led',
    labelKey: 'glp.guided.cards.doctorLed.label',
    label: 'Doctor-Led',
    titleKey: 'glp.guided.cards.doctorLed.title',
    title: 'Doctor-Led',
    bodyKey: 'glp.guided.cards.doctorLed.body',
    body: 'A physician manages your therapy right from the first dose – adjusting based on your response, handling nausea, and knowing when to change or stop.',
    imageLabel: 'Doctor-led',
    imageSrc: GLP_GUIDED_IMAGE_SRC['doctor-led'],
  },
  {
    id: 'nutritionist-driven',
    labelKey: 'glp.guided.cards.nutritionistDriven.label',
    label: 'Nutritionist-driven',
    titleKey: 'glp.guided.cards.nutritionistDriven.title',
    title: 'Nutritionist-driven',
    bodyKey: 'glp.guided.cards.nutritionistDriven.body',
    body: 'A nutritionist creates a plan for smaller appetites: enough protein to maintain muscle, easy-to-digest foods during nausea, and a structure to keep you nourished without losing lean mass.',
    imageLabel: 'Nutritionist-driven',
    imageSrc: GLP_GUIDED_IMAGE_SRC['nutritionist-driven'],
  },
  {
    id: 'action-plan',
    labelKey: 'glp.guided.cards.actionPlan.label',
    label: 'Performance Coaching',
    titleKey: 'glp.guided.cards.actionPlan.title',
    title: 'Performance Coaching',
    bodyKey: 'glp.guided.cards.actionPlan.body',
    body: 'A coach who knows your data translates what your markers are saying – how to train and protect muscle, how to supplement a smaller appetite, what to watch and when to check back in. Guided care that adjusts to your body, every week, quarter, and across the full program.',
    imageLabel: 'Performance coaching',
    imageSrc: GLP_GUIDED_IMAGE_SRC['action-plan'],
  },
  {
    id: 'biointelligence-ai',
    labelKey: 'glp.guided.cards.biointelligenceAi.label',
    label: 'Biointelligence AI',
    titleKey: 'glp.guided.cards.biointelligenceAi.title',
    title: 'Biointelligence AI',
    bodyKey: 'glp.guided.cards.biointelligenceAi.body',
    body: 'Markers in your body are read and correlated into\nsignals for your plan. This means monitoring fat loss while preserving muscle, tracking glucose and metabolism responses, and detecting blood shifts before you feel them.',
    imageLabel: 'Biointelligence AI',
    imageSrc: GLP_GUIDED_IMAGE_SRC['biointelligence-ai'],
  },
  {
    id: 'ultrahuman-vision',
    labelKey: 'glp.guided.cards.ultrahumanVision.label',
    label: 'Ultrahuman Vision',
    titleKey: 'glp.guided.cards.ultrahumanVision.title',
    title: 'Ultrahuman Vision',
    bodyKey: 'glp.guided.cards.ultrahumanVision.body',
    body: 'All your health data, securely stored in one dashboard. Track every scan from the first visit to the last as you progress through the program. ',
    imageLabel: 'Ultrahuman Vision',
    imageSrc: GLP_GUIDED_IMAGE_SRC['ultrahuman-vision'],
  },
];

export type GlpTimelineRow = {
  id: string;
  phaseKey: string;
  phase: string;
  titleKey: string;
  title: string;
  bodyKey: string;
  body: string;
  /** Reuses a buy-page thumbnail for the row icon (placeholder, no modal). */
  comparisonId?: string;
  oneMonth: boolean | string;
  sixMonth: boolean | string;
  oneMonthKey?: string;
  sixMonthKey?: string;
};

/** Know Your Program — "Journey" toggle: week-by-week timeline. */
export const GLP_JOURNEY_TIMELINE: ReadonlyArray<GlpTimelineRow> = [
  {
    id: 'online-consult',
    phase: 'Pre Program',
    title: 'Online Consult',
    body: 'Comprehensive review of your medical history and health goals with our Lab team',
    comparisonId: 'longevity-doctor-consultation',
    oneMonth: true,
    sixMonth: true,
  },
  {
    id: 'blood-testing',
    phase: 'Pre Program',
    title: 'Blood Testing',
    body: 'Blood Vision panel for a comprehensive and gold-standard understanding of your blood markers',
    comparisonId: 'blood-vision',
    oneMonth: true,
    sixMonth: true,
  },
  {
    id: 'blood-testing-base',
    phase: '',
    title: 'Base panel',
    body: '',
    comparisonId: 'blood-vision',
    oneMonth: true,
    sixMonth: true,
  },
  {
    id: 'blood-testing-peak',
    phase: '',
    title: 'Peak panel',
    body: '',
    comparisonId: 'blood-vision',
    oneMonth: false,
    sixMonth: true,
  },
  {
    id: 'baseline-checks',
    phase: 'Day 1',
    title: 'Baseline Checks',
    body: 'A visit to the Lab covering markers across Retina, BP, RMR, DEXA, MSK, Grip, to map out your personalized plan',
    comparisonId: 'body-composition-analysis',
    oneMonth: true,
    sixMonth: true,
  },
  {
    id: 'baseline-checks-expanded',
    phase: '',
    title: 'Expanded testing with VO2 Max, and Cognition assessment',
    body: '',
    comparisonId: 'body-composition-analysis',
    oneMonth: false,
    sixMonth: true,
  },
  {
    id: 'consult-1',
    phase: 'Week 1',
    title: 'Consult 1',
    body: 'Custom starting dose, day-wise nutrition plan, weekly training plan',
    comparisonId: 'longevity-doctor-consultation',
    oneMonth: true,
    sixMonth: true,
  },
  {
    id: 'consult-1-cgm',
    phase: '',
    title: 'Includes CGM',
    body: '',
    comparisonId: 'longevity-doctor-consultation',
    oneMonth: false,
    sixMonth: true,
  },
  {
    id: 'lab-visit-1',
    phase: 'Week 2',
    title: 'Lab Visit',
    body: 'In-house administration of Dose 2 and review of weekly progress',
    comparisonId: 'vitals',
    oneMonth: true,
    sixMonth: true,
  },
  {
    id: 'lab-visit-2',
    phase: 'Week 3',
    title: 'Lab Visit',
    body: 'In-house administration of Dose 3 and review of weekly progress',
    comparisonId: 'vitals',
    oneMonth: true,
    sixMonth: true,
  },
  {
    id: 'consult-2',
    phase: 'Week 4',
    title: 'Consult 2',
    body: "A one-on-one with your doctor to review how your first four weeks have gone. You'll go over your progress together, see where things have improved, and fine-tune the plan for what's next.",
    comparisonId: 'longevity-doctor-consultation',
    oneMonth: true,
    sixMonth: true,
  },
  {
    id: 'testing-w4',
    phase: 'Week 4',
    title: 'Testing',
    body: 'A retest of vital markers and a blood draw, plus a comprehensive review of results for a full picture on your progress',
    comparisonId: 'blood-vision',
    oneMonth: false,
    sixMonth: true,
  },
  {
    id: 'consult-3',
    phase: 'Month 2',
    title: 'Consult 3',
    body: 'BP & Grip across Week 6 & 8, monthly CGM',
    comparisonId: 'grip-strength',
    oneMonth: false,
    sixMonth: true,
  },
  {
    id: 'consult-4',
    phase: 'Month 3',
    title: 'Consult 4',
    body: 'BP & Grip across Week 10 & 12, monthly CGM, retest of vital markers (blood draw, DEXA, OCT, VO2) for a mid-program view',
    comparisonId: 'body-composition-analysis',
    oneMonth: false,
    sixMonth: true,
  },
  {
    id: 'consult-5',
    phase: 'Month 4',
    title: 'Consult 5',
    body: 'BP & Grip in Week 16, monthly CGM',
    comparisonId: 'grip-strength',
    oneMonth: false,
    sixMonth: true,
  },
  {
    id: 'consult-6',
    phase: 'Month 5',
    title: 'Consult 6',
    body: 'BP & Grip in Week 20, monthly CGM',
    comparisonId: 'grip-strength',
    oneMonth: false,
    sixMonth: true,
  },
  {
    id: 'step-off',
    phase: 'Month 6',
    title: 'Step Off / Repeat',
    body: 'Full bloods, Retina, Cognition, BP, RMR, DEXA, CPET, MSK, VO2, Grip, for a personalized plan to begin your taper-off journey',
    comparisonId: 'vo2-max',
    oneMonth: false,
    sixMonth: true,
  },
].map((row) => ({
  ...row,
  phaseKey: `glp.knowYourProgram.timeline.${row.id}.phase`,
  titleKey: `glp.knowYourProgram.timeline.${row.id}.title`,
  bodyKey: `glp.knowYourProgram.timeline.${row.id}.body`,
  oneMonthKey: `glp.knowYourProgram.timeline.${row.id}.oneMonth`,
  sixMonthKey: `glp.knowYourProgram.timeline.${row.id}.sixMonth`,
}));

export type GlpJourneyQuestion = {
  id: string;
  questionKey: string;
  question: string;
  answerKey: string;
  answer: string;
  timestampKey: string;
  timestamp: string;
  imageLabel: string;
  backgroundSrc?: string;
};

export type GlpJourneyChatStyle = {
  overlayOpacity: number;
  overlayBlurPx: number;
  bubbleUsesLightFill: boolean;
};

/** Per-card overlay + bubble tokens from Figma (4060:1167, 4075:1750, 4075:1749). */
export const GLP_JOURNEY_CHAT_STYLES: Readonly<
  Record<string, GlpJourneyChatStyle>
> = {
  foggy: {
    overlayOpacity: 0.24,
    overlayBlurPx: 48,
    bubbleUsesLightFill: true,
  },
  'energy-crashes': {
    overlayOpacity: 0.24,
    overlayBlurPx: 52,
    bubbleUsesLightFill: false,
  },
  'train-hard': {
    overlayOpacity: 0.3,
    overlayBlurPx: 52,
    bubbleUsesLightFill: false,
  },
};

export const GLP_JOURNEY_CHAT_CARD_IDS = [
  'foggy',
  'energy-crashes',
  'train-hard',
] as const;

/** Questions Along The Journey — embla single-card carousel. */
export const GLP_JOURNEY_QUESTIONS: ReadonlyArray<GlpJourneyQuestion> = [
  {
    id: 'foggy',
    questionKey: 'glp.journeyQuestions.items.foggy.question',
    question: 'I feel a bit foggy lately. Should I be concerned?',
    answerKey: 'glp.journeyQuestions.items.foggy.answer',
    answer:
      "Usually electrolyte imbalance or glycogen depletion. Not alarming - let's review your sleep and glucose data first.",
    timestampKey: 'glp.journeyQuestions.items.foggy.timestamp',
    timestamp: '11:14 AM',
    imageLabel: 'Progress review',
    backgroundSrc: GLP_JOURNEY_CHAT_BG_SRC.foggy,
  },
  {
    id: 'energy-crashes',
    questionKey: 'glp.journeyQuestions.items.energyCrashes.question',
    question: 'How can I handle my energy crashes?',
    answerKey: 'glp.journeyQuestions.items.energyCrashes.answer',
    answer:
      "Usually fuelling and timing on a smaller appetite. Let's check your glucose and nutrition data, then tweak meal timing and protein to smooth the dips.",
    timestampKey: 'glp.journeyQuestions.items.energyCrashes.timestamp',
    timestamp: '11:14 AM',
    imageLabel: 'Nutrition check-in',
    backgroundSrc: GLP_JOURNEY_CHAT_BG_SRC['energy-crashes'],
  },
  {
    id: 'train-hard',
    questionKey: 'glp.journeyQuestions.items.trainHard.question',
    question: 'Should I still train hard while eating this little?',
    answerKey: 'glp.journeyQuestions.items.trainHard.answer',
    answer:
      "The priority is to protect muscle: resistance work and enough protein are what signal your body to hold lean mass in a deficit. We'll dial intensity to your recovery and fueling, and track it against your grip and DEXA.",
    timestampKey: 'glp.journeyQuestions.items.trainHard.timestamp',
    timestamp: '11:14 AM',
    imageLabel: 'Coaching session',
    backgroundSrc: GLP_JOURNEY_CHAT_BG_SRC['train-hard'],
  },
  {
    id: 'nausea',
    questionKey: 'glp.journeyQuestions.items.nausea.question',
    question: 'How can I manage my nausea?',
    answerKey: 'glp.journeyQuestions.items.nausea.answer',
    answer:
      'Slower gastric emptying makes you feel full and queasy. Try smaller, lower-fat meals and steady hydration.',
    timestampKey: 'glp.journeyQuestions.items.nausea.timestamp',
    timestamp: '11:40 AM',
    imageLabel: 'Nutrition plan',
  },
  {
    id: 'grip',
    questionKey: 'glp.journeyQuestions.items.grip.question',
    question: 'Why does my grip feel weaker this week?',
    answerKey: 'glp.journeyQuestions.items.grip.answer',
    answer:
      'Good question. Calorie restriction suppresses creatine synthesis. Your grip will recover once intake stabilizes - usually by week 3.',
    timestampKey: 'glp.journeyQuestions.items.grip.timestamp',
    timestamp: '11:42 AM',
    imageLabel: 'Clinician consult',
  },
];

/** Journey carousel — Figma chat cards only (4060:1167, 4075:1750, 4075:1749). */
export const GLP_JOURNEY_CHAT_CARDS: ReadonlyArray<GlpJourneyQuestion> =
  GLP_JOURNEY_CHAT_CARD_IDS.map(
    (id) => GLP_JOURNEY_QUESTIONS.find((item) => item.id === id)!,
  );

export type GlpScienceCard = {
  id: string;
  sourceKey: string;
  source: string;
  dateKey: string;
  date: string;
  titleKey: string;
  title: string;
  href: string;
  imageLabel: string;
  imageSrc: string;
  imageSrcMobile: string;
};

/** We Read The Science — embla carousel of references. */
export const GLP_SCIENCE_CARDS: ReadonlyArray<GlpScienceCard> = [
  {
    id: 'glp-files',
    sourceKey: 'glp.science.cards.glpFiles.source',
    source: 'ultrahuman',
    dateKey: 'glp.science.cards.glpFiles.date',
    date: '25 March 2025',
    titleKey: 'glp.science.cards.glpFiles.title',
    title:
      'The GLP-1 Files: A Data-Driven Journey Through Tirzepatide, Tracked with 134 Days of Continuous Wearable Data',
    href: '/science/studies/science-ultrahuman-com-studies-glpfiles',
    imageLabel: 'Ultrahuman',
    imageSrc: GLP_SCIENCE_IMAGE_SRC['glp-files'],
    imageSrcMobile: GLP_SCIENCE_IMAGE_SRC_MOBILE['glp-files'],
  },
  {
    id: 'semaglutide-cv',
    sourceKey: 'glp.science.cards.semaglutideCv.source',
    source: 'the new england journal of medicine',
    dateKey: 'glp.science.cards.semaglutideCv.date',
    date: '11 November 2023',
    titleKey: 'glp.science.cards.semaglutideCv.title',
    title: 'Semaglutide and Cardiovascular Outcomes in Obesity (SELECT)',
    href: 'https://www.nejm.org/doi/full/10.1056/NEJMoa2307563',
    imageLabel: 'NEJM',
    imageSrc: GLP_SCIENCE_IMAGE_SRC['semaglutide-cv'],
    imageSrcMobile: GLP_SCIENCE_IMAGE_SRC_MOBILE['semaglutide-cv'],
  },
  {
    id: 'semaglutide-mash',
    sourceKey: 'glp.science.cards.semaglutideMash.source',
    source: 'the new england journal of medicine',
    dateKey: 'glp.science.cards.semaglutideMash.date',
    date: '30 april 2025',
    titleKey: 'glp.science.cards.semaglutideMash.title',
    title:
      'Phase 3 Trial of Semaglutide in Metabolic Dysfunction–Associated Steatohepatitis',
    href: 'https://www.nejm.org/doi/full/10.1056/NEJMoa2413258',
    imageLabel: 'NEJM',
    imageSrc: GLP_SCIENCE_IMAGE_SRC['semaglutide-mash'],
    imageSrcMobile: GLP_SCIENCE_IMAGE_SRC_MOBILE['semaglutide-mash'],
  },
  {
    id: 'semaglutide-kidney',
    sourceKey: 'glp.science.cards.semaglutideKidney.source',
    source: 'the new england journal of medicine',
    dateKey: 'glp.science.cards.semaglutideKidney.date',
    date: '24 may 2024',
    titleKey: 'glp.science.cards.semaglutideKidney.title',
    title: 'Semaglutide on CKD in Type 2 Diabetes (FLOW)',
    href: 'https://www.nejm.org/doi/full/10.1056/NEJMoa2403347',
    imageLabel: 'NEJM',
    imageSrc: GLP_SCIENCE_IMAGE_SRC['semaglutide-kidney'],
    imageSrcMobile: GLP_SCIENCE_IMAGE_SRC_MOBILE['semaglutide-kidney'],
  },
];

export type GlpFaqItem = { id: string; questionKey: string; question: string; answerKey: string; answer: string };

/** Still got questions — GLP FAQ (copy from Figma). */
export const GLP_FAQ_ITEMS: ReadonlyArray<GlpFaqItem> = [
  {
    id: 'who-is-for',
    questionKey: 'glp.faq.items.whoIsFor.question',
    question: 'Who is the program for?',
    answerKey: 'glp.faq.items.whoIsFor.answer',
    answer:
      'The GLP-1 Led Longevity Program is designed for those who want to lose fat in a supervised, measured way – with a doctor overseeing their care, their whole body tracked, and their muscle and strength protected as the fat comes off. Whether the program is right for you is confirmed by your doctor at your first consultation, based on your health history and goals.',
  },
  {
    id: 'existing',
    questionKey: 'glp.faq.items.existing.question',
    question: "Can I join if I'm already on a GLP-1 medication?",
    answerKey: 'glp.faq.items.existing.answer',
    answer:
      'Yes. Your doctor reviews where you are now and brings your care into the program from there. Bring your current prescription and any recent results to your first consultation so they have the full picture.',
  },
  {
    id: 'medication',
    questionKey: 'glp.faq.items.medication.question',
    question: 'Do I purchase the medication from Ultrahuman?',
    answerKey: 'glp.faq.items.medication.answer',
    answer:
      "No. We don't sell or supply the medication. Our in-house doctor prescribes what's appropriate for your health history, and our pharmacy team helps you access it through a licensed seller. The medication is billed separately and is not included in the program cost.",
  },
  {
    id: 'different',
    questionKey: 'glp.faq.items.different.question',
    question: 'What makes it different from other weight programs?',
    answerKey: 'glp.faq.items.different.answer',
    answer:
      "Most programs track one number on a scale. We measure across ten systems – body composition, blood, heart, glucose, strength, vision, fitness and cognition – and read them together, so the plan responds to what's actually happening in your body, not just the scale.",
  },
  {
    id: 'data',
    questionKey: 'glp.faq.items.data.question',
    question: 'Is my health data private and secure?',
    answerKey: 'glp.faq.items.data.answer',
    answer:
      "Yes. The scans, blood work and metrics from your program are your data. They're stored securely with encryption, access is limited to the clinical team involved in your care, and they're used to guide your program – not sold to advertisers or third parties. You can request a copy or deletion of your data at any time.",
  },
];
